import z from "zod";

const materialsErrorTypeValues = [
  "PLACEHOLDER_ERROR",
  "UNSUPPORTED_MATERIALS_SUPPLIER",
  "MISSING_SRS_API_CREDENTIALS",
  "NO_PDF_DATA",
  "INVALID_SRS_ORDER_RESPONSE",
  "INVALID_SRS_INVOICE_RESPONSE",
  "INVALID_SRS_ORDER_ID",
  "INVALID_SRS_INVOICE_ID",
  "INVALID_QXO_AUTH_RESPONSE",
  "INVALID_QXO_ORDER_RESPONSE",
  "INVALID_QXO_RECENT_ORDERS_RESPONSE",
  "QXO_AUTH_LOCKOUT",
  "QXO_LOGIN_FAILED",
  "QXO_ORDER_VOIDED",
] as const;

const materialsErrorTypeSchema = z.enum(materialsErrorTypeValues);
const MaterialsErrorType = materialsErrorTypeSchema.enum;
type MaterialsErrorType = z.infer<typeof materialsErrorTypeSchema>;

type ErrorDetails = {
  sentryDescription: string;
};

export type MaterialsErrors =
  | { errorType: typeof MaterialsErrorType.PLACEHOLDER_ERROR; data: undefined }
  | { errorType: typeof MaterialsErrorType.UNSUPPORTED_MATERIALS_SUPPLIER; data: { supplier: string } }
  | { errorType: typeof MaterialsErrorType.MISSING_SRS_API_CREDENTIALS; data: undefined }
  | {
      errorType: typeof MaterialsErrorType.NO_PDF_DATA;
      data: {
        invoiceNumber: string;
        errorEnvelope?: Array<{ code: number; type: string; value: string; key?: string | null }>;
        responseShape?: { type: string; keys?: string[]; length?: number; preview?: string };
      };
    }
  | { errorType: typeof MaterialsErrorType.INVALID_SRS_ORDER_RESPONSE; data: { error: z.ZodError; response: unknown } }
  | {
      errorType: typeof MaterialsErrorType.INVALID_SRS_INVOICE_RESPONSE;
      data: { error: z.ZodError; response: unknown };
    }
  | {
      errorType: typeof MaterialsErrorType.INVALID_SRS_ORDER_ID;
      data: { orderNumber: string; message: string };
    }
  | {
      errorType: typeof MaterialsErrorType.INVALID_SRS_INVOICE_ID;
      data: { invoiceNumber: string; message: string };
    }
  | {
      errorType: typeof MaterialsErrorType.INVALID_QXO_AUTH_RESPONSE;
      data: {
        statusCode?: number;
        errorEnvelope?: Array<{ code: number; type: string; value: string; key?: string | null }>;
        // Diagnostic shape capture (keys/types only — no values, no secrets).
        responseShape?: { type: string; keys?: string[]; length?: number; preview?: string };
        contentType?: string;
        zodPaths?: string[];
      };
    }
  | {
      errorType: typeof MaterialsErrorType.INVALID_QXO_ORDER_RESPONSE;
      data: {
        orderId: string;
        statusCode?: number;
        zodPaths?: string[];
        errorEnvelope?: Array<{ code: number; type: string; value: string; key?: string | null }>;
        responseShape?: { type: string; keys?: string[]; length?: number; preview?: string };
      };
    }
  | {
      errorType: typeof MaterialsErrorType.INVALID_QXO_RECENT_ORDERS_RESPONSE;
      data: {
        page?: number;
        zodPaths?: string[];
        responseShape?: { type: string; keys?: string[]; length?: number; preview?: string };
      };
    }
  | {
      errorType: typeof MaterialsErrorType.QXO_AUTH_LOCKOUT;
      data: { cooldownUntil: string; failureCount: number };
    }
  | {
      errorType: typeof MaterialsErrorType.QXO_LOGIN_FAILED;
      data:
        | {
            reason: "non_200_message_code";
            messageCode: string;
            attempts: number;
            messages: Array<{ code: number; type: string; value: string; key?: string | null }>;
          }
        | { reason: "guest_account" }
        | { reason: "missing_session_cookie" };
    }
  | {
      errorType: typeof MaterialsErrorType.QXO_ORDER_VOIDED;
      data: { orderId: string; message: string };
    };

export const materialsErrorDetails: Record<MaterialsErrorType, ErrorDetails> = {
  [MaterialsErrorType.PLACEHOLDER_ERROR]: {
    sentryDescription: "Materials external service error",
  },
  [MaterialsErrorType.UNSUPPORTED_MATERIALS_SUPPLIER]: {
    sentryDescription: "Unsupported materials supplier",
  },
  [MaterialsErrorType.MISSING_SRS_API_CREDENTIALS]: {
    sentryDescription: "Missing SRS API credentials",
  },
  [MaterialsErrorType.NO_PDF_DATA]: {
    sentryDescription: "No PDF data",
  },
  [MaterialsErrorType.INVALID_SRS_ORDER_RESPONSE]: {
    sentryDescription: "Invalid SRS order response",
  },
  [MaterialsErrorType.INVALID_SRS_INVOICE_RESPONSE]: {
    sentryDescription: "Invalid SRS invoice response",
  },
  [MaterialsErrorType.INVALID_SRS_ORDER_ID]: {
    sentryDescription: "Invalid SRS order ID - invoiceId used instead of orderId",
  },
  [MaterialsErrorType.INVALID_SRS_INVOICE_ID]: {
    sentryDescription: "Invalid SRS invoice ID - orderId used instead of invoiceId",
  },
  [MaterialsErrorType.INVALID_QXO_AUTH_RESPONSE]: {
    sentryDescription: "Invalid QXO auth response",
  },
  [MaterialsErrorType.INVALID_QXO_ORDER_RESPONSE]: {
    sentryDescription: "Invalid QXO order response",
  },
  [MaterialsErrorType.INVALID_QXO_RECENT_ORDERS_RESPONSE]: {
    sentryDescription: "Invalid QXO recent orders response",
  },
  [MaterialsErrorType.QXO_AUTH_LOCKOUT]: {
    sentryDescription: "QXO password-auth circuit breaker active",
  },
  [MaterialsErrorType.QXO_LOGIN_FAILED]: {
    sentryDescription: "QXO login rejected by upstream",
  },
  [MaterialsErrorType.QXO_ORDER_VOIDED]: {
    sentryDescription: "QXO order is voided or cancelled in Mincron",
  },
};
